import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Loader2, Save, Check, X, Minus, Camera, Image as ImageIcon, Sparkles, CheckCheck, RotateCcw,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VAL_OPTIONS = [
  { value: "perfetto", label: "Perfetto", score: 100, color: "emerald" },
  { value: "buono", label: "Buono", score: 80, color: "lavender" },
  { value: "sufficiente", label: "Sufficiente", score: 60, color: "amber" },
  { value: "insufficiente", label: "Insufficiente", score: 30, color: "rose" },
];

function StatoButton({ stato, current, onClick, icon: Icon, label, color, testid }) {
  const active = current === stato;
  const palette = {
    emerald: active ? "bg-emerald-500 text-white border-emerald-500" : "border-border hover:border-emerald-300 text-emerald-700 dark:text-emerald-300",
    rose: active ? "bg-rose-500 text-white border-rose-500" : "border-border hover:border-rose-300 text-rose-700 dark:text-rose-300",
    slate: active ? "bg-slate-500 text-white border-slate-500" : "border-border hover:border-slate-300 text-slate-600 dark:text-slate-300",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`flex-1 h-9 rounded-lg border text-xs font-medium transition-all active:scale-[0.97] flex items-center justify-center gap-1 ${palette[color]}`}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" /> {label}
    </button>
  );
}

export default function ControlloCameraDettaglio() {
  const { id, roomId } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [data, setData] = useState(null); // { template, rooms[], check, ... }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [voci, setVoci] = useState({}); // key="sezione::voce" → { stato, nota }
  const [valutazione, setValutazione] = useState(null);
  const [notaCamera, setNotaCamera] = useState("");
  const [fotos, setFotos] = useState([]); // {id, url}
  // v100 — Confirm modal per "Tutto OK" / "Reset sezione" quando ci sono voci già compilate
  const [bulkConfirm, setBulkConfirm] = useState(null); // { mode: 'ok'|'reset', sezione, voci, counts:{conformi,nonConformi} }

  const room = data?.rooms?.find((r) => r.room_id === roomId);
  const template = data?.template;
  const isReadOnly = data?.is_locked;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/checks/${id}/rooms`);
      setData(d);
      const r = d.rooms.find((x) => x.room_id === roomId);
      if (r?.record) {
        const map = {};
        for (const v of r.record.voci || []) map[`${v.sezione}::${v.voce}`] = { stato: v.stato, nota: v.nota || "" };
        setVoci(map);
        setValutazione(r.record.valutazione || null);
        setNotaCamera(r.record.nota_camera || "");
        setFotos((r.record.foto_ids || []).map((fid) => ({ id: fid, url: `${BACKEND_URL}/api/uploads/${fid}` })));
      } else {
        setVoci({});
        setValutazione(null);
        setNotaCamera("");
        setFotos([]);
      }
    } catch (e) {
      toast.error(formatApiError(e));
      navigate(`/controlli/${id}`);
    } finally { setLoading(false); }
  }, [id, roomId, navigate]);
  useEffect(() => { load(); }, [load]);

  const setVoce = (sezione, voce, patch) => {
    const key = `${sezione}::${voce}`;
    setVoci((prev) => ({ ...prev, [key]: { ...(prev[key] || { stato: null, nota: "" }), ...patch } }));
  };
  const getVoce = (sezione, voce) => voci[`${sezione}::${voce}`] || { stato: null, nota: "" };

  // Stats
  const totalVoci = (template?.sezioni || []).reduce((s, sec) => s + sec.voci.length, 0);
  const compiledVoci = Object.values(voci).filter((v) => v.stato).length;
  const issues = Object.values(voci).filter((v) => v.stato === "non_ok").length;

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Max 8MB"); return; }
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) { toast.error("Solo JPG/PNG/WebP"); return; }
    if (fotos.length >= 3) { toast.error("Massimo 3 foto per camera"); return; }
    setUploading(true);
    const { compressPhoto } = await import("@/lib/imageCompression");
    const compressed = await compressPhoto(file);
    const form = new FormData();
    form.append("file", compressed, compressed.name || file.name);
    form.append("daily_check_id", id);
    form.append("hotel_id", data.check.hotel_id);
    try {
      const { data: up } = await api.post("/photos/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setFotos((arr) => [...arr, { id: up.id, url: `${BACKEND_URL}${up.url}` }]);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const removeFoto = async (fid) => {
    try { await api.delete(`/photos/${fid}`); setFotos((arr) => arr.filter((f) => f.id !== fid)); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const save = async () => {
    if (!valutazione) { toast.error("Seleziona la valutazione complessiva"); return; }
    setSaving(true);
    try {
      const vociPayload = [];
      for (const sec of template.sezioni) {
        for (const v of sec.voci) {
          const cur = getVoce(sec.nome, v);
          vociPayload.push({ sezione: sec.nome, voce: v, stato: cur.stato || null, nota: cur.nota || "" });
        }
      }
      await api.put(`/checks/${id}/rooms/${roomId}`, {
        voci: vociPayload,
        valutazione,
        nota_camera: notaCamera,
        foto_ids: fotos.map((f) => f.id),
      });
      toast.success(`Camera ${room?.numero || ""} salvata`);
      navigate(`/controlli/${id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  // v100 — Bulk helpers: "Tutto OK" / "Reset sezione"
  const _sezioneStats = (sec) => {
    const stati = sec.voci.map((v) => getVoce(sec.nome, v).stato);
    const compilate = stati.filter((s) => s).length;
    const conformi = stati.filter((s) => s === "ok").length;
    const nonConformi = stati.filter((s) => s === "non_ok").length;
    return { total: sec.voci.length, compilate, conformi, nonConformi, allConformi: conformi === sec.voci.length && sec.voci.length > 0 };
  };

  const _applyBulk = (sezione, voci, mode) => {
    setVoci((prev) => {
      const next = { ...prev };
      for (const v of voci) {
        const key = `${sezione}::${v}`;
        if (mode === "ok") {
          next[key] = { stato: "ok", nota: (prev[key] && prev[key].nota) || "" };
        } else {
          next[key] = { stato: null, nota: "" };
        }
      }
      return next;
    });
  };

  const _scrollToNextIncomplete = (currentSezioneName) => {
    if (!template?.sezioni) return;
    const idx = template.sezioni.findIndex((s) => s.nome === currentSezioneName);
    for (let i = idx + 1; i < template.sezioni.length; i++) {
      const nextSec = template.sezioni[i];
      const stats = _sezioneStats(nextSec);
      if (stats.compilate < stats.total) {
        const el = document.querySelector(`[data-testid="section-card-${nextSec.nome}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        return;
      }
    }
  };

  const handleBulkClick = (sec) => {
    if (isReadOnly) return;
    const stats = _sezioneStats(sec);
    // Scenario 3: tutte conformi → reset
    if (stats.allConformi) {
      if (window.confirm(`Resettare le ${stats.total} voci della sezione "${sec.nome}"?`)) {
        _applyBulk(sec.nome, sec.voci, "reset");
        toast.success(`Sezione ${sec.nome} resettata`);
      }
      return;
    }
    // Scenario 1: nessuna voce compilata → marca tutte OK senza conferma
    if (stats.compilate === 0) {
      _applyBulk(sec.nome, sec.voci, "ok");
      toast.success(`Sezione ${sec.nome} completata`);
      setTimeout(() => _scrollToNextIncomplete(sec.nome), 150);
      return;
    }
    // Scenario 2: alcune voci già compilate → modal conferma
    setBulkConfirm({
      mode: "ok",
      sezione: sec.nome,
      voci: sec.voci,
      counts: { conformi: stats.conformi, nonConformi: stats.nonConformi, compilate: stats.compilate, total: stats.total },
    });
  };

  const confirmBulk = () => {
    if (!bulkConfirm) return;
    _applyBulk(bulkConfirm.sezione, bulkConfirm.voci, bulkConfirm.mode);
    toast.success(`Sezione ${bulkConfirm.sezione} aggiornata`);
    const sezName = bulkConfirm.sezione;
    setBulkConfirm(null);
    setTimeout(() => _scrollToNextIncomplete(sezName), 150);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="h-6 w-6 animate-spin text-lavender-500" /></div>;
  }
  if (!data || !room || !template) {
    return (
      <div className="space-y-3">
        <Button variant="ghost" onClick={() => navigate(`/controlli/${id}`)}><ArrowLeft className="h-4 w-4" /> Torna al controllo</Button>
        <Card className="p-6"><p className="text-sm text-muted-foreground">Camera non trovata o non disponibile per questo controllo.</p></Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-24" data-testid="room-detail-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/controlli/${id}`)} data-testid="back-to-check">
          <ArrowLeft className="h-4 w-4" /> Indietro
        </Button>
        <div className="text-xs text-muted-foreground">Controllo Camere · {data.check.data_prevista}</div>
      </div>

      <Card className="p-5 border-l-4 border-l-lavender-400" style={{ backgroundImage: "linear-gradient(135deg, rgba(231,215,246,0.30) 0%, transparent 60%)" }}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-lavender-700 dark:text-lavender-300 mb-1">Camera in controllo</div>
            <h1 className="font-display tracking-tight">N° {room.numero}</h1>
            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1.5">
              {room.piano && <span>Piano {room.piano}</span>}
              {room.tipologia && <><span>·</span><span>{room.tipologia}</span></>}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">Progressione</div>
            <div className="text-2xl font-semibold tabular-nums">{compiledVoci}<span className="text-base text-muted-foreground"> / {totalVoci}</span></div>
            {issues > 0 && <div className="text-[11px] text-rose-600 mt-0.5">{issues} criticità rilevate</div>}
          </div>
        </div>
      </Card>

      {/* Sezioni */}
      {template.sezioni.map((sec, sIdx) => {
        const stats = _sezioneStats(sec);
        const isReset = stats.allConformi;
        const isPartial = stats.compilate > 0 && !stats.allConformi;
        return (
        <Card key={sIdx} className="p-5" data-testid={`section-card-${sec.nome}`}>
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{sec.nome}</span>
              {isPartial && (
                <span
                  data-testid={`section-counter-${sec.nome}`}
                  className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-muted/60 text-muted-foreground tabular-nums"
                >
                  {stats.conformi}/{stats.total} conformi
                </span>
              )}
            </div>
            {!isReadOnly && (
              <button
                type="button"
                onClick={() => handleBulkClick(sec)}
                data-testid={`bulk-${sec.nome}`}
                title={isReset ? "Riporta tutte le voci della sezione a non compilato" : "Marca tutte le voci di questa sezione come Conforme"}
                className={`inline-flex items-center gap-1.5 h-7 px-3 rounded-full text-[11px] font-semibold border transition-all active:scale-[0.97] ${
                  isReset
                    ? "bg-muted/50 text-muted-foreground border-border/60 hover:bg-muted"
                    : "bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600 shadow-sm shadow-emerald-500/20"
                }`}
              >
                {isReset ? <><RotateCcw className="h-3 w-3" /> Reset sezione</> : <><CheckCheck className="h-3.5 w-3.5" /> Tutto OK</>}
              </button>
            )}
          </div>
          <div className="space-y-2">
            {sec.voci.map((v) => {
              const cur = getVoce(sec.nome, v);
              return (
                <div key={v} className="rounded-xl border border-border/60 bg-background/30 p-3" data-testid={`voce-${sec.nome}-${v}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                    <span className="text-sm font-medium">{v}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <StatoButton stato="ok" current={cur.stato} onClick={() => !isReadOnly && setVoce(sec.nome, v, { stato: cur.stato === "ok" ? null : "ok" })}
                      icon={Check} label="OK" color="emerald" testid={`stato-ok-${sec.nome}-${v}`} />
                    <StatoButton stato="non_ok" current={cur.stato} onClick={() => !isReadOnly && setVoce(sec.nome, v, { stato: cur.stato === "non_ok" ? null : "non_ok" })}
                      icon={X} label="Non OK" color="rose" testid={`stato-nonok-${sec.nome}-${v}`} />
                    <StatoButton stato="na" current={cur.stato} onClick={() => !isReadOnly && setVoce(sec.nome, v, { stato: cur.stato === "na" ? null : "na" })}
                      icon={Minus} label="N/A" color="slate" testid={`stato-na-${sec.nome}-${v}`} />
                  </div>
                  {cur.stato === "non_ok" && (
                    <Textarea
                      value={cur.nota}
                      onChange={(e) => setVoce(sec.nome, v, { nota: e.target.value })}
                      placeholder="Descrivi la criticità (opzionale)…"
                      className="mt-2 text-xs bg-background/40 min-h-[60px]"
                      disabled={isReadOnly}
                      data-testid={`nota-${sec.nome}-${v}`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      );
      })}

      {/* Valutazione complessiva */}
      <Card className="p-5" data-testid="valutazione-card">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-3 flex items-center gap-1.5">
          <Sparkles className="h-3 w-3 text-lavender-500" /> Valutazione complessiva della camera
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {VAL_OPTIONS.map((opt) => {
            const active = valutazione === opt.value;
            const palette = {
              emerald: active ? "bg-emerald-500 text-white border-emerald-500 shadow-md shadow-emerald-500/20" : "border-border hover:border-emerald-300",
              lavender: active ? "bg-lavender-500 text-white border-lavender-500 shadow-md shadow-lavender-500/20" : "border-border hover:border-lavender-300",
              amber: active ? "bg-amber-500 text-white border-amber-500 shadow-md shadow-amber-500/20" : "border-border hover:border-amber-300",
              rose: active ? "bg-rose-500 text-white border-rose-500 shadow-md shadow-rose-500/20" : "border-border hover:border-rose-300",
            };
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => !isReadOnly && setValutazione(opt.value)}
                data-testid={`val-${opt.value}`}
                className={`rounded-xl border px-3 py-3 text-center transition-all active:scale-[0.97] ${palette[opt.color]}`}
              >
                <div className="text-sm font-semibold">{opt.label}</div>
                <div className={`text-[10px] mt-0.5 ${active ? "text-white/80" : "text-muted-foreground"}`}>{opt.score}/100</div>
              </button>
            );
          })}
        </div>

        <div className="mt-4 space-y-1.5">
          <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Nota camera (opzionale)</label>
          <Textarea
            value={notaCamera}
            onChange={(e) => setNotaCamera(e.target.value)}
            placeholder="Note generali per la camera…"
            disabled={isReadOnly}
            className="bg-background/40 min-h-[70px]"
            data-testid="nota-camera"
          />
        </div>
      </Card>

      {/* Foto opzionali */}
      <Card className="p-5" data-testid="foto-card">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-3 flex items-center gap-1.5">
          <ImageIcon className="h-3 w-3" /> Foto opzionali ({fotos.length}/3)
        </div>
        <div className="flex items-start gap-2 flex-wrap">
          {fotos.map((f) => (
            <div key={f.id} className="relative group">
              <img src={f.url} alt="" className="h-20 w-20 rounded-lg object-cover border border-border" />
              {!isReadOnly && (
                <button type="button" onClick={() => removeFoto(f.id)} className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-rose-500 text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity">×</button>
              )}
            </div>
          ))}
          {!isReadOnly && fotos.length < 3 && (
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="h-20 w-20 rounded-lg border border-dashed border-border hover:border-lavender-400 flex items-center justify-center text-muted-foreground hover:text-lavender-600 transition-colors" data-testid="upload-photo">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-5 w-5" />}
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleFileChange} />
        </div>
        {!isReadOnly && (
          <p className="text-[10px] text-muted-foreground mt-3" data-testid="retention-hint">
            Compresse automaticamente e conservate <strong>30 giorni</strong>.
          </p>
        )}
      </Card>

      {/* Sticky save bar */}
      {!isReadOnly && (
        <div className="fixed bottom-0 left-0 right-0 md:left-[var(--sidebar-w,16rem)] z-40 ih-glass-strong border-t border-border/60 px-4 py-3 flex items-center justify-between gap-3" data-testid="save-bar">
          <div className="text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">{compiledVoci}/{totalVoci}</span> voci · valutazione: <span className="font-medium text-foreground">{valutazione || "—"}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to={`/controlli/${id}`}>Annulla</Link>
            </Button>
            <Button onClick={save} disabled={saving || !valutazione} className="gap-1.5" data-testid="save-room">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Salva camera
            </Button>
          </div>
        </div>
      )}
      {isReadOnly && (
        <Card className="p-4 bg-amber-50/40 dark:bg-amber-950/20 border-amber-200/50 dark:border-amber-900/40">
          <div className="text-xs text-amber-700 dark:text-amber-300">Controllo completato — la camera è in sola lettura.</div>
        </Card>
      )}

      {/* v100 — Modal conferma "Tutto OK" quando ci sono voci già compilate */}
      <Dialog open={!!bulkConfirm} onOpenChange={(o) => !o && setBulkConfirm(null)}>
        <DialogContent className="ih-glass-strong" data-testid="bulk-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              Sovrascrivere le voci della sezione?
            </DialogTitle>
            <DialogDescription>
              {bulkConfirm && (
                <>
                  Stai per marcare come <b>Conforme</b> tutte le <b>{bulkConfirm.counts.total}</b> voci della sezione <b>{bulkConfirm.sezione}</b>.
                  <br />
                  Attualmente: <span className="font-medium text-emerald-600 dark:text-emerald-400">{bulkConfirm.counts.conformi} conformi</span>
                  {" · "}
                  <span className="font-medium text-rose-600 dark:text-rose-400">{bulkConfirm.counts.nonConformi} non conformi</span>
                  . Le note sulle voci non conformi verranno rimosse.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setBulkConfirm(null)} data-testid="bulk-cancel">
              Annulla
            </Button>
            <Button onClick={confirmBulk} className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1.5" data-testid="bulk-confirm">
              <CheckCheck className="h-4 w-4" /> Sovrascrivi tutto come Conforme
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
